import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Brain,
  Lock,
  Mail,
  Building,
  User,
  ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const questions = [
  {
    id: 1,
    question: "Do employees at your organization use AI tools like ChatGPT, Claude, or Copilot?",
    options: [
      { text: "Yes, widely used", score: 30, risk: "high" },
      { text: "Yes, some employees", score: 20, risk: "medium" },
      { text: "Not sure", score: 25, risk: "high" },
      { text: "No, it's prohibited", score: 5, risk: "low" }
    ]
  },
  {
    id: 2,
    question: "Does your organization have a formal AI usage policy?",
    options: [
      { text: "Yes, comprehensive policy", score: 0, risk: "low" },
      { text: "Yes, but basic guidelines", score: 10, risk: "medium" },
      { text: "Currently developing one", score: 15, risk: "medium" },
      { text: "No policy in place", score: 25, risk: "high" }
    ]
  },
  {
    id: 3,
    question: "Do you monitor which AI tools employees are using?",
    options: [
      { text: "Yes, real-time monitoring", score: 0, risk: "low" },
      { text: "Periodic audits only", score: 10, risk: "medium" },
      { text: "Rely on self-reporting", score: 20, risk: "high" },
      { text: "No monitoring at all", score: 30, risk: "high" }
    ]
  },
  {
    id: 4,
    question: "Do employees handle Protected Health Information (PHI) or patient data?",
    options: [
      { text: "Yes, daily", score: 25, risk: "high" },
      { text: "Yes, occasionally", score: 15, risk: "medium" },
      { text: "Limited access only", score: 10, risk: "medium" },
      { text: "No PHI access", score: 0, risk: "low" }
    ]
  },
  {
    id: 5,
    question: "Could employees copy/paste patient information into AI chatbots?",
    options: [
      { text: "Yes, it's technically possible", score: 30, risk: "high" },
      { text: "Possible but unlikely", score: 20, risk: "medium" },
      { text: "We have DLP tools to prevent this", score: 5, risk: "low" },
      { text: "No, systems are air-gapped", score: 0, risk: "low" }
    ]
  },
  {
    id: 6,
    question: "What would happen if PHI was accidentally shared with an AI service?",
    options: [
      { text: "We'd have no way of knowing", score: 30, risk: "high" },
      { text: "We might find out eventually", score: 20, risk: "medium" },
      { text: "We'd be alerted within hours", score: 10, risk: "medium" },
      { text: "It would be blocked automatically", score: 0, risk: "low" }
    ]
  },
  {
    id: 7,
    question: "How prepared are you for a HIPAA audit regarding AI tool usage?",
    options: [
      { text: "Fully prepared with documentation", score: 0, risk: "low" },
      { text: "Somewhat prepared", score: 10, risk: "medium" },
      { text: "Not very prepared", score: 20, risk: "high" },
      { text: "This is a blind spot for us", score: 30, risk: "high" }
    ]
  }
];

const getRiskLevel = (score) => {
  if (score >= 120) return { level: "Critical", color: "red", icon: XCircle };
  if (score >= 80) return { level: "High", color: "orange", icon: AlertTriangle };
  if (score >= 40) return { level: "Medium", color: "yellow", icon: AlertTriangle };
  return { level: "Low", color: "green", icon: CheckCircle };
};

const getRecommendations = (score, answers) => {
  const recs = [];
  
  if (score >= 80) {
    recs.push({
      priority: "Critical",
      text: "Implement real-time AI usage monitoring immediately",
      icon: Shield
    });
  }
  
  if (answers[1]?.score >= 15) {
    recs.push({
      priority: "High",
      text: "Develop and enforce a comprehensive AI usage policy",
      icon: Lock
    });
  }
  
  if (answers[2]?.score >= 20) {
    recs.push({
      priority: "High", 
      text: "Deploy monitoring tools to track AI service access",
      icon: Brain
    });
  }
  
  if (answers[4]?.score >= 20) {
    recs.push({
      priority: "Critical",
      text: "Implement PHI/PII detection to prevent data leaks to AI",
      icon: AlertTriangle
    });
  }
  
  if (answers[5]?.score >= 20) {
    recs.push({
      priority: "High",
      text: "Set up real-time alerts for sensitive data exposure",
      icon: Mail
    });
  }
  
  if (recs.length === 0) {
    recs.push({
      priority: "Maintain",
      text: "Continue current security practices and consider periodic audits",
      icon: CheckCircle
    });
  }
  
  return recs;
};

export default function RiskAssessment() {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [leadInfo, setLeadInfo] = useState({ name: "", email: "", company: "", role: "" });
  const [submitting, setSubmitting] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);

  const totalScore = Object.values(answers).reduce((sum, a) => sum + (a?.score || 0), 0);
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const riskInfo = getRiskLevel(totalScore);
  const recommendations = getRecommendations(totalScore, answers);

  const handleAnswer = (option) => {
    setAnswers({ ...answers, [currentQuestion]: option });
    
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      setTimeout(() => setShowResults(true), 300);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/leads/risk-assessment`, {
        ...leadInfo,
        score: totalScore,
        risk_level: riskInfo.level,
        answers: Object.entries(answers).map(([q, a]) => ({
          question: questions[q].question,
          answer: a.text,
          risk: a.risk
        })),
        recommendations: recommendations.map(r => r.text)
      });
      
      setLeadCaptured(true);
      toast.success("Thank you! Check your email for your full report.");
    } catch (error) {
      console.error("Failed to submit lead:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const restartAssessment = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setShowLeadCapture(false);
    setLeadCaptured(false);
    setLeadInfo({ name: "", email: "", company: "", role: "" });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
              <Shield className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold font-[Chivo]">AI Governance Office</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {!showResults ? (
          <>
            {/* Progress */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-muted-foreground mb-2">
                <span>Question {currentQuestion + 1} of {questions.length}</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Question Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestion}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-2xl">
                      {questions[currentQuestion].question}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {questions[currentQuestion].options.map((option, i) => (
                      <button
                        key={i}
                        onClick={() => handleAnswer(option)}
                        className={`w-full p-4 text-left rounded-sm border transition-all ${
                          answers[currentQuestion]?.text === option.text
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/50 hover:bg-muted/30"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{option.text}</span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </button>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            {currentQuestion > 0 && (
              <Button
                variant="ghost"
                className="mt-4"
                onClick={() => setCurrentQuestion(currentQuestion - 1)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous Question
              </Button>
            )}
          </>
        ) : !showLeadCapture ? (
          /* Results */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className={`bg-card border-2 ${
              riskInfo.color === "red" ? "border-red-500" :
              riskInfo.color === "orange" ? "border-orange-500" :
              riskInfo.color === "yellow" ? "border-yellow-500" :
              "border-green-500"
            }`}>
              <CardHeader className="text-center pb-2">
                <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-4 ${
                  riskInfo.color === "red" ? "bg-red-500/20" :
                  riskInfo.color === "orange" ? "bg-orange-500/20" :
                  riskInfo.color === "yellow" ? "bg-yellow-500/20" :
                  "bg-green-500/20"
                }`}>
                  <riskInfo.icon className={`h-10 w-10 ${
                    riskInfo.color === "red" ? "text-red-500" :
                    riskInfo.color === "orange" ? "text-orange-500" :
                    riskInfo.color === "yellow" ? "text-yellow-500" :
                    "text-green-500"
                  }`} />
                </div>
                <CardTitle className="text-3xl">Your AI Risk Score</CardTitle>
                <div className={`text-6xl font-black font-[Chivo] mt-2 ${
                  riskInfo.color === "red" ? "text-red-500" :
                  riskInfo.color === "orange" ? "text-orange-500" :
                  riskInfo.color === "yellow" ? "text-yellow-500" :
                  "text-green-500"
                }`}>
                  {totalScore}/200
                </div>
                <CardDescription className="text-lg mt-2">
                  Risk Level: <span className={`font-bold ${
                    riskInfo.color === "red" ? "text-red-500" :
                    riskInfo.color === "orange" ? "text-orange-500" :
                    riskInfo.color === "yellow" ? "text-yellow-500" :
                    "text-green-500"
                  }`}>{riskInfo.level}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Risk Summary */}
                <div className={`p-4 rounded-sm ${
                  riskInfo.color === "red" ? "bg-red-500/10 border border-red-500/30" :
                  riskInfo.color === "orange" ? "bg-orange-500/10 border border-orange-500/30" :
                  riskInfo.color === "yellow" ? "bg-yellow-500/10 border border-yellow-500/30" :
                  "bg-green-500/10 border border-green-500/30"
                }`}>
                  {riskInfo.level === "Critical" && (
                    <p className="text-red-400">
                      <strong>Critical Risk:</strong> Your organization has significant exposure to AI-related data leaks. 
                      Without immediate action, you face substantial HIPAA violation risk. The average healthcare data 
                      breach costs $10.9 million.
                    </p>
                  )}
                  {riskInfo.level === "High" && (
                    <p className="text-orange-400">
                      <strong>High Risk:</strong> Your organization has notable gaps in AI governance. Employees may be 
                      inadvertently exposing sensitive data to AI services without proper oversight.
                    </p>
                  )}
                  {riskInfo.level === "Medium" && (
                    <p className="text-yellow-400">
                      <strong>Medium Risk:</strong> You have some protections in place, but there are opportunities to 
                      strengthen your AI governance posture and reduce potential exposure.
                    </p>
                  )}
                  {riskInfo.level === "Low" && (
                    <p className="text-green-400">
                      <strong>Low Risk:</strong> Your organization appears to have good AI governance practices in place. 
                      Continue monitoring and stay vigilant as AI usage evolves.
                    </p>
                  )}
                </div>

                {/* Recommendations Preview */}
                <div>
                  <h3 className="font-bold text-lg mb-3">Key Recommendations:</h3>
                  <ul className="space-y-2">
                    {recommendations.slice(0, 2).map((rec, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <rec.icon className={`h-5 w-5 shrink-0 mt-0.5 ${
                          rec.priority === "Critical" ? "text-red-500" :
                          rec.priority === "High" ? "text-orange-500" :
                          "text-green-500"
                        }`} />
                        <span>{rec.text}</span>
                      </li>
                    ))}
                    {recommendations.length > 2 && (
                      <li className="text-muted-foreground text-sm pl-7">
                        + {recommendations.length - 2} more recommendations in full report
                      </li>
                    )}
                  </ul>
                </div>

                {/* CTA */}
                <div className="pt-4 border-t border-border">
                  <Button 
                    className="w-full glow-primary" 
                    size="lg"
                    onClick={() => setShowLeadCapture(true)}
                  >
                    Get Your Full Report & Action Plan
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="text-center text-sm text-muted-foreground mt-2">
                    Includes detailed analysis and step-by-step remediation guide
                  </p>
                </div>
              </CardContent>
            </Card>

            <Button variant="ghost" className="mt-4" onClick={restartAssessment}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retake Assessment
            </Button>
          </motion.div>
        ) : !leadCaptured ? (
          /* Lead Capture Form */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-card border-border">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Get Your Full AI Risk Report</CardTitle>
                <CardDescription>
                  Enter your details to receive a comprehensive report with personalized recommendations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Full Name *</label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          required
                          placeholder="John Smith"
                          className="pl-10"
                          value={leadInfo.name}
                          onChange={(e) => setLeadInfo({...leadInfo, name: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Work Email *</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          required
                          type="email"
                          placeholder="john@hospital.org"
                          className="pl-10"
                          value={leadInfo.email}
                          onChange={(e) => setLeadInfo({...leadInfo, email: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Organization *</label>
                      <div className="relative">
                        <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          required
                          placeholder="Memorial Hospital"
                          className="pl-10"
                          value={leadInfo.company}
                          onChange={(e) => setLeadInfo({...leadInfo, company: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Your Role</label>
                      <Input
                        placeholder="IT Director, CISO, etc."
                        value={leadInfo.role}
                        onChange={(e) => setLeadInfo({...leadInfo, role: e.target.value})}
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                    {submitting ? "Sending..." : "Send My Free Report"}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    By submitting, you agree to receive your report and occasional updates about AI governance. 
                    We respect your privacy and will never share your information.
                  </p>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          /* Thank You */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-card border-primary">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="mx-auto h-20 w-20 rounded-full bg-primary/20 flex items-center justify-center mb-6">
                  <CheckCircle className="h-10 w-10 text-primary" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
                <p className="text-muted-foreground mb-6">
                  Your personalized AI Risk Report is on its way to <strong>{leadInfo.email}</strong>
                </p>
                
                <div className="bg-muted/30 rounded-sm p-4 mb-6 text-left">
                  <h3 className="font-semibold mb-2">What happens next?</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Check your email for the full report (within 5 minutes)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Review your personalized recommendations
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Schedule a free consultation to discuss solutions
                    </li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => navigate("/register")}>
                    Start Free Trial
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/")}>
                    Back to Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
